import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { SidebarItemProps, MenuItem } from '@/types';
import { getIconComponent } from './Sidebar';

interface SidebarItemDropdownProps extends SidebarItemProps {
  childrenItems?: MenuItem[];
  currentPath: string;
  isCollapsed: boolean;
  onClick: (path: string) => void;
}

const SidebarItemDropdown: React.FC<SidebarItemDropdownProps> = ({
  text,
  icon,
  isSelected,
  isCollapsed,
  childrenItems = [],
  currentPath,
  onClick,
  openDropdown,
  onDropdownToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  /**
   * Prefer longest path match so nested child routes highlight correctly
   * (e.g. /users/:slug vs a longer sibling path under the same parent).
   */
  const matchingChildren = childrenItems.filter((item) => {
    if (!item.path) return false;
    return currentPath === item.path || currentPath.startsWith(item.path + '/');
  });
  const bestChildMatch = [...matchingChildren].sort(
    (a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0)
  )[0];
  const hasSelectedChild = Boolean(bestChildMatch);

  /**
   * openDropdown === null → auto-open when a child page is active
   * openDropdown === ''   → user closed; stay closed even with selected child
   * openDropdown === text → this menu is explicitly open
   */
  const isOpen = openDropdown === text || (hasSelectedChild && openDropdown === null);
  
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDropdownToggle) {
      return;
    }
    /**
     * One click closes even when open via selected child (openDropdown === null).
     * '' = user closed; null = allow auto-open again after navigation.
     */
    onDropdownToggle(isOpen ? '' : text);
  };
  
  // Collapse dropdown on mobile when a child is clicked
  const handleChildClick = (path: string) => {
    if (isMobile && onDropdownToggle) {
      onDropdownToggle('');
    }
    onClick(path);
  };

  return (
    <>
      <ListItem disablePadding sx={{ position: 'relative' }}>
        <ListItemButton
          selected={isSelected || isOpen}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls={`dropdown-list-${text}`}
          sx={{
            minHeight: 52,
            px: isCollapsed ? 1 : 2,
            borderRadius: '0 8px 8px 0',
            marginRight: 1,
            marginLeft: 0,
            transition: 'all 0.2s ease-in-out',
            backgroundColor: isOpen ? 'rgba(59, 136, 128, 0.08)' : 'transparent',
            color: isOpen ? 'primary.main' : 'text.secondary',
            fontWeight: isOpen ? 600 : 400,
            '&:hover': {
              backgroundColor: 'rgba(59, 136, 128, 0.04)',
              color: 'primary.main',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(59, 136, 128, 0.08)',
              color: 'primary.main',
              fontWeight: 600,
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
              '& .MuiListItemText-primary': {
                color: 'primary.main',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: isCollapsed ? 32 : 40,
              color: isOpen ? 'primary.main' : 'inherit',
              transition: 'color 0.2s ease-in-out',
            }}
          >
            {icon}
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: isOpen ? 600 : 400,
                  fontSize: '0.95rem',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          )}
          {!isCollapsed && (
            <IconButton
              size="small"
              onClick={handleToggle}
              edge="end"
              aria-label={isOpen ? `Collapse ${text}` : `Expand ${text}`}
              sx={{ ml: 1, color: 'inherit' }}
              tabIndex={0}
            >
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen && !isCollapsed} timeout="auto" unmountOnExit>
        <List component="div" disablePadding id={`dropdown-list-${text}`}>
          {childrenItems.map((child) => {
            /**
             * Prefer longest matching path so nested routes highlight the
             * most specific child under this parent menu.
             */
            const matchingChildren = childrenItems.filter((item) => {
              if (!item.path) return false;
              return currentPath === item.path || currentPath.startsWith(item.path + '/');
            });
            const bestMatch = [...matchingChildren].sort(
              (a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0)
            )[0];
            const isChildSelected = Boolean(child.path && bestMatch?.path === child.path);

            return (
              <SidebarItem
                key={child.text}
                text={child.text}
                icon={getIconComponent(child.iconName)}
                path={child.path!}
                isSelected={isChildSelected}
                isCollapsed={isCollapsed}
                onClick={handleChildClick}
                currentPath={currentPath}
                sx={{
                  pl: isCollapsed ? 2 : 5,
                  py: 1.2,
                  borderLeft: isChildSelected ? '3px solid #3B8880' : '3px solid transparent',
                  backgroundColor: isChildSelected ? 'rgba(59, 136, 128, 0.10)' : 'transparent',
                  fontWeight: isChildSelected ? 600 : 400,
                  color: isChildSelected ? 'primary.main' : 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 136, 128, 0.08)',
                    color: 'primary.main',
                  },
                }}
              />
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

const SidebarItem: React.FC<SidebarItemProps & {
  childrenItems?: MenuItem[];
  currentPath?: string;
}> = ({
  text,
  icon,
  path,
  isSelected,
  isCollapsed,
  onClick,
  childrenItems,
  currentPath,
  openDropdown,
  onDropdownToggle,
  sx,
}) => {
  if (childrenItems && childrenItems.length > 0 && currentPath !== undefined) {
    return (
      <SidebarItemDropdown
        text={text}
        icon={icon}
        isSelected={isSelected}
        isCollapsed={isCollapsed}
        childrenItems={childrenItems}
        currentPath={currentPath}
        onClick={onClick}
        path={path || ''}
        openDropdown={openDropdown}
        onDropdownToggle={onDropdownToggle}
      />
    );
  }
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={() => path && onClick(path)}
        sx={{
          minHeight: 48,
          px: isCollapsed ? 1 : 2,
          position: 'relative',
          borderRadius: '0 8px 8px 0',
          marginRight: 1,
          marginLeft: 0,
          transition: 'all 0.2s ease-in-out',
          backgroundColor: 'transparent',
          color: 'text.secondary',
          '&.Mui-selected': {
            backgroundColor: 'rgba(59, 136, 128, 0.08)',
            color: 'primary.main',
            fontWeight: 600,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: 'primary.main',
              borderRadius: '0 2px 2px 0',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
            '& .MuiListItemText-primary': {
              fontWeight: 600,
              color: 'primary.main',
            },
            '&:hover': {
              backgroundColor: 'rgba(59, 136, 128, 0.12)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(59, 136, 128, 0.04)',
            color: 'primary.main',
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
          },
          ...sx,
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: isCollapsed ? 0 : 40,
            color: 'inherit',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {icon}
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={text}
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: isSelected ? 600 : 400,
                fontSize: '0.875rem',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default SidebarItem; 